import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

type ParallaxProps = {
  /**
   * Scroll multiplier. `0` = locked to page scroll (no parallax), positive = moves slower
   * than scroll (appears further away), negative = moves faster (closer to viewer).
   * Typical subtle range: 0.1 to 0.5. Default: 0.2.
   */
  speed?: number;
  /** Horizontal parallax factor using the same convention. Default: 0. */
  speedX?: number;
  /** Opacity is faded in-out relative to viewport center when > 0. Default: 0. */
  fade?: number;
  /** Maximum translate in pixels (clamps the parallax amount). Default: 200. */
  clamp?: number;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  /** Set to false to disable parallax entirely (still renders children). */
  enabled?: boolean;
};

// Shared scroll listener so N Parallax instances piggyback on a single RAF tick.
let subscribers: Array<() => void> = [];
let listenerAttached = false;
let rafId = 0;

function runSubscribers() {
  rafId = 0;
  for (const cb of subscribers) cb();
}

function onScrollOrResize() {
  if (rafId) return;
  rafId = requestAnimationFrame(runSubscribers);
}

function addSubscriber(cb: () => void): () => void {
  subscribers.push(cb);
  if (!listenerAttached) {
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    listenerAttached = true;
  }
  return () => {
    subscribers = subscribers.filter((fn) => fn !== cb);
    if (subscribers.length === 0 && listenerAttached) {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      listenerAttached = false;
    }
  };
}

/**
 * Subtle scroll parallax wrapper.
 * Uses the element's distance from viewport center to drive `translate3d` (and optionally
 * `opacity`). Applies zero-cost identity transforms when out of view and respects
 * `prefers-reduced-motion`.
 */
export default function Parallax({
  speed = 0.2,
  speedX = 0,
  fade = 0,
  clamp = 200,
  className = '',
  style,
  children,
  enabled = true,
}: ParallaxProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;

      // Skip heavy work if the element is well outside the viewport.
      if (rect.bottom < -viewportH * 0.5 || rect.top > viewportH * 1.5) return;

      const elementCenter = rect.top + rect.height / 2;
      const viewportCenter = viewportH / 2;
      const offset = elementCenter - viewportCenter; // negative when above center
      const ty = Math.max(-clamp, Math.min(clamp, -offset * speed));
      const tx = Math.max(-clamp, Math.min(clamp, -offset * speedX));
      el.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0)`;

      if (fade > 0) {
        const norm = Math.min(1, Math.abs(offset) / viewportH);
        el.style.opacity = String(Math.max(0, 1 - norm * fade));
      }
    };

    update();
    const unsubscribe = addSubscriber(update);
    return () => {
      unsubscribe();
    };
  }, [speed, speedX, fade, clamp, enabled]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ willChange: 'transform', ...style }}
    >
      {children}
    </div>
  );
}
